<template>
    <Teleport to="body">
        <div v-if="award" class="fso-overlay" role="dialog" aria-modal="true" aria-labelledby="sgaOverlayTitle">
            <div class="fso-backdrop" @click="close" />

            <div class="fso-card" :class="{ 'fso-card--has-image': theme.image }" :style="cssVars">
                <div class="fso-orb fso-orb--1" aria-hidden="true" />
                <div class="fso-orb fso-orb--2" aria-hidden="true" />

                <button class="fso-close" aria-label="Close" @click="close">&times;</button>

                <!-- Hero image -->
                <div v-if="theme.image" class="fso-image-wrap">
                    <img :src="theme.image" alt="" class="fso-image" aria-hidden="true">
                    <div class="fso-image-overlay" />
                    <div class="fso-decorations" aria-hidden="true">
                        <span v-for="(deco, i) in theme.decorations" :key="i" class="fso-deco" :class="`fso-deco--${i + 1}`" v-html="deco" />
                    </div>
                </div>

                <!-- Inline decorations -->
                <div v-else class="fso-decorations fso-decorations--inline" aria-hidden="true">
                    <span v-for="(deco, i) in theme.decorations" :key="i" class="fso-deco" :class="`fso-deco--${i + 1}`" v-html="deco" />
                </div>

                <div class="fso-body">
                    <div class="fso-badge fso-anim-in" style="animation-delay:0.15s">{{ theme.badge }}</div>

                    <h2 id="sgaOverlayTitle" class="fso-title fso-anim-in" style="animation-delay:0.25s">
                        {{ theme.title }}
                    </h2>

                    <div class="fso-amount fso-anim-in" style="animation-delay:0.35s">
                        <img src="https://sm.naughtycdn.com/images/landers/prize/NA-Star.png" alt="Star" class="fso-amount-star">
                        <span class="fso-amount-number">{{ award.stars.toLocaleString() }}</span>
                        <span class="fso-amount-label">Stars</span>
                    </div>

                    <p class="fso-text fso-anim-in" style="animation-delay:0.42s">{{ dynamicText }}</p>

                    <div class="fso-actions fso-anim-in" style="animation-delay:0.52s">
                        <div class="fso-ctas">
                            <a :href="theme.cta_url" class="fso-btn fso-btn--primary" @click="track('Browse Auditions Button Clicked')">
                                {{ theme.cta }}
                            </a>
                            <a :href="theme.cta2_url" class="fso-btn fso-btn--outline" @click="track('Create Pitch Button Clicked')">
                                {{ theme.cta2 }}
                            </a>
                        </div>
                        <button class="fso-btn fso-btn--secondary" @click="close('maybe-later')">Maybe Later</button>
                    </div>
                </div>
            </div>
        </div>
    </Teleport>
</template>

<script setup>
import { computed, watch } from 'vue'

const props = defineProps({
    award: {
        type: Object,
        default: null, // { theme, stars, goal_stars, campaign_bonus_stars, goal_label, campaign_completed, autocamp }
    },
})

const emit = defineEmits(['close'])

const THEMES = {
    default: {
        badge:       'Gift Claimed',
        title:       "You've got stars — use them.",
        text:        'Sponsor an audition to help a performer get filmed, or pitch your own scene idea and let the community decide.',
        cta:         'Browse Auditions',
        cta_url:     '/feed/auditions',
        cta2:        'Create a Pitch',
        cta2_url:    '/studio/create-scene',
        image:       null,
        decorations: Array(5).fill('<img src="https://sm.naughtycdn.com/images/landers/prize/NA-Star.png" alt="" style="width:var(--fso-deco-size,26px);height:var(--fso-deco-size,26px);filter:drop-shadow(0 0 6px rgba(96,165,250,0.7));">'),
        css: {
            '--fso-card-bg':      'linear-gradient(145deg, #0f172a 0%, #1e293b 60%, #0f172a 100%)',
            '--fso-border':       'rgba(59,130,246,0.35)',
            '--fso-shadow-color': 'rgba(59,130,246,0.18)',
            '--fso-badge-bg':     'linear-gradient(135deg,#3b82f6,#2563eb)',
            '--fso-title-color':  '#f1f5f9',
            '--fso-amount-color': '#60a5fa',
            '--fso-text-color':   '#94a3b8',
            '--fso-btn-bg':       '#3b82f6',
            '--fso-btn-hover':    '#2563eb',
            '--fso-close-color':  '#64748b',
            '--fso-deco-size':    '28px',
            '--fso-glow':         'rgba(59,130,246,0.5)',
        },
    },
    'mothers-day': {
        badge:       'Welcome Back 🌷',
        title:       "You've got stars — use them.",
        text:        "As a thank-you for coming back, we've loaded your account with complimentary stars. Sponsor an audition to help a performer get filmed, or pitch your own scene idea and let the community decide.",
        cta:         'Browse Auditions',
        cta_url:     '/feed/auditions',
        cta2:        'Create a Pitch',
        cta2_url:    '/studio/create-scene',
        image:       'https://aws-lg.naughtycdn.com/mediaAssets/100514/ma_modal.png',
        decorations: ['🌸', '🌹', '💐', '🌺', '🌷'],
        css: {
            '--fso-card-bg':      'linear-gradient(145deg, #1c0a14 0%, #2e1222 55%, #1c0a14 100%)',
            '--fso-border':       'rgba(236,72,153,0.4)',
            '--fso-shadow-color': 'rgba(236,72,153,0.2)',
            '--fso-badge-bg':     'linear-gradient(135deg,#e879a0,#be185d)',
            '--fso-title-color':  '#fce7f3',
            '--fso-amount-color': '#f9a8d4',
            '--fso-text-color':   '#e4aec5',
            '--fso-btn-bg':       '#be185d',
            '--fso-btn-hover':    '#9d174d',
            '--fso-close-color':  '#9d6b7f',
            '--fso-deco-size':    '30px',
            '--fso-glow':         'rgba(236,72,153,0.6)',
        },
    },
}

const theme = computed(() => THEMES[props.award?.theme] ?? THEMES.default)

const cssVars = computed(() =>
    Object.entries(theme.value.css).map(([k, v]) => `${k}:${v}`).join(';')
)

const dynamicText = computed(() => {
    if (!props.award) return theme.value.text

    const { goal_stars, campaign_bonus_stars, goal_label, campaign_completed } = props.award

    // If we have goal info, create a custom message
    if (goal_stars > 0 && goal_label) {
        let message = `You've got ${goal_stars.toLocaleString()} ${goal_stars === 1 ? 'star' : 'stars'} for ${goal_label}`

        if (campaign_completed && campaign_bonus_stars > 0) {
            message += ` and ${campaign_bonus_stars.toLocaleString()} extra ${campaign_bonus_stars === 1 ? 'star' : 'stars'} for completing the campaign`
        }

        message += '!'
        return message
    }

    // Fallback to theme text
    return theme.value.text
})

watch(() => props.award, (val) => {
    if (val) track('Campaign Overlay Displayed')
})

function track(event) {
    if (typeof LTIAnalytics !== 'undefined') {
        LTIAnalytics.track(event, { autocamp: props.award?.autocamp })
    }
}

function close(reason = 'close') {
    if (reason === 'maybe-later') track('Maybe Later Button Clicked')
    else track('Close Button Clicked')
    emit('close')
}

// Close on Escape
if (typeof window !== 'undefined') {
    window.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && props.award) close()
    })
}
</script>

<style scoped>
.fso-overlay {
    position: fixed;
    inset: 0;
    z-index: 99999;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
}

.fso-backdrop {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.8);
    backdrop-filter: blur(6px);
    cursor: pointer;
    animation: fso-fade-in 0.3s ease both;
}

.fso-card {
    position: relative;
    z-index: 1;
    background: var(--fso-card-bg);
    border: 1px solid var(--fso-border);
    border-radius: 22px;
    max-width: 460px;
    width: 100%;
    text-align: center;
    overflow: hidden;
    box-shadow: 0 32px 80px rgba(0, 0, 0, 0.7), 0 0 60px var(--fso-shadow-color);
    animation: fso-card-in 0.45s cubic-bezier(0.34, 1.56, 0.64, 1) both;
}

.fso-orb {
    position: absolute;
    border-radius: 50%;
    pointer-events: none;
    filter: blur(50px);
    opacity: 0.18;
    animation: fso-orb-drift 8s ease-in-out infinite alternate;
}
.fso-orb--1 { width: 220px; height: 220px; background: var(--fso-glow); top: -60px; left: -60px; animation-duration: 9s; }
.fso-orb--2 { width: 180px; height: 180px; background: var(--fso-glow); bottom: -50px; right: -40px; animation-duration: 11s; animation-direction: alternate-reverse; }

.fso-close {
    position: absolute;
    top: 12px;
    right: 14px;
    z-index: 10;
    background: rgba(0, 0, 0, 0.35);
    border: none;
    color: #fff;
    font-size: 22px;
    line-height: 1;
    cursor: pointer;
    padding: 4px 9px;
    border-radius: 50%;
    backdrop-filter: blur(4px);
    transition: background 0.2s, transform 0.2s;
}
.fso-close:hover { background: rgba(0, 0, 0, 0.6); transform: scale(1.1); }

.fso-image-wrap { position: relative; width: 100%; height: 180px; overflow: hidden; }
.fso-image { width: 100%; height: 100%; object-fit: cover; object-position: center top; display: block; animation: fso-img-zoom 12s ease-in-out infinite alternate; }
.fso-image-overlay { position: absolute; inset: 0; background: linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.55) 70%, rgba(0,0,0,0.9) 100%); }

.fso-decorations { position: absolute; inset: 0; pointer-events: none; }
.fso-decorations--inline { position: relative; height: 72px; margin-bottom: 4px; }

.fso-deco {
    position: absolute;
    font-size: var(--fso-deco-size, 26px);
    opacity: 0;
    line-height: 1;
    animation: fso-deco-in 0.6s ease forwards, fso-float 4s ease-in-out infinite;
}
.fso-deco--1 { top: 18px; left: 18%;  animation-delay: 0.1s, 0.7s; }
.fso-deco--2 { top: 6px;  left: 50%;  margin-left: -18px; font-size: calc(var(--fso-deco-size, 26px) + 12px); animation-delay: 0.02s, 0.6s; }
.fso-deco--3 { top: 18px; right: 18%; animation-delay: 0.16s, 0.8s; }
.fso-deco--4 { top: 40px; left: 8%;   font-size: calc(var(--fso-deco-size, 26px) - 8px); animation-delay: 0.24s, 1.0s; }
.fso-deco--5 { top: 40px; right: 8%;  font-size: calc(var(--fso-deco-size, 26px) - 8px); animation-delay: 0.24s, 1.2s; }

.fso-body { padding: 28px 36px 32px; }
.fso-card--has-image .fso-body { padding-top: 22px; }

.fso-badge { display: inline-block; padding: 5px 14px; background: var(--fso-badge-bg); color: #fff; font-size: 11px; font-weight: 700; letter-spacing: 1.2px; text-transform: uppercase; border-radius: 20px; margin-bottom: 14px; }
.fso-title { font-size: 22px; font-weight: 700; color: var(--fso-title-color); margin: 0 0 16px; line-height: 1.3; }
.fso-text  { color: var(--fso-text-color); font-size: 13px; line-height: 1.7; margin: 0 0 24px; }

.fso-amount { display: inline-flex; align-items: center; justify-content: center; gap: 8px; margin-bottom: 16px; }
.fso-amount-star   { width: 28px; height: 28px; animation: fso-star-spin 6s linear infinite; filter: drop-shadow(0 0 6px var(--fso-glow)); }
.fso-amount-number { font-size: 32px; font-weight: 800; color: var(--fso-amount-color); line-height: 1; text-shadow: 0 0 20px var(--fso-glow); animation: fso-amount-pulse 3s ease-in-out infinite; }
.fso-amount-label  { font-size: 16px; font-weight: 600; color: var(--fso-amount-color); opacity: 0.8; align-self: flex-end; padding-bottom: 3px; }

.fso-actions { display: flex; flex-direction: column; gap: 10px; }
.fso-ctas { display: flex; gap: 10px; }
.fso-ctas .fso-btn { flex: 1; }

.fso-btn { display: inline-flex; align-items: center; justify-content: center; gap: 6px; padding: 12px 16px; border-radius: 10px; font-size: 13px; font-weight: 700; cursor: pointer; text-decoration: none; transition: background 0.2s, transform 0.18s, box-shadow 0.2s; }
.fso-btn--primary  { background: var(--fso-btn-bg); color: #fff; border: none; box-shadow: 0 4px 16px var(--fso-shadow-color); }
.fso-btn--primary:hover  { background: var(--fso-btn-hover); color: #fff; transform: translateY(-2px); }
.fso-btn--outline  { background: transparent; color: var(--fso-amount-color); border: 1px solid var(--fso-border); }
.fso-btn--outline:hover  { background: rgba(255,255,255,0.05); border-color: var(--fso-amount-color); transform: translateY(-2px); }
.fso-btn--secondary { background: transparent; color: var(--fso-close-color); border: 1px solid rgba(255,255,255,0.1); font-weight: 500; font-size: 12px; }
.fso-btn--secondary:hover { color: #aaa; border-color: rgba(255,255,255,0.22); }

.fso-anim-in { opacity: 0; animation: fso-content-in 0.5s ease forwards; }

@keyframes fso-fade-in    { from { opacity: 0; } to { opacity: 1; } }
@keyframes fso-card-in    { from { opacity: 0; transform: translateY(32px) scale(0.95); } to { opacity: 1; transform: translateY(0) scale(1); } }
@keyframes fso-orb-drift  { from { transform: translate(0, 0) scale(1); } to { transform: translate(30px, 20px) scale(1.15); } }
@keyframes fso-img-zoom   { from { transform: scale(1); } to { transform: scale(1.06); } }
@keyframes fso-deco-in    { from { opacity: 0; transform: scale(0.2) translateY(14px); } to { opacity: 1; transform: scale(1) translateY(0); } }
@keyframes fso-float      { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-7px); } }
@keyframes fso-star-spin  { 0%, 100% { transform: rotate(-8deg) scale(1); } 50% { transform: rotate(8deg) scale(1.15); } }
@keyframes fso-amount-pulse { 0%, 100% { text-shadow: 0 0 16px var(--fso-glow); } 50% { text-shadow: 0 0 32px var(--fso-glow); } }
@keyframes fso-content-in { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }

@media (max-width: 480px) {
    .fso-body          { padding: 20px 20px 26px; }
    .fso-title         { font-size: 18px; }
    .fso-amount-number { font-size: 26px; }
    .fso-image-wrap    { height: 140px; }
    .fso-ctas          { flex-direction: column; }
}
</style>
